<template>
  <ModalDialog
    :header-text="`${currentContact != null ? 'Modify' : 'Add New'} Contact`"
    @close-modal="closeModal"
    contentClass="contact-dialog-content"
    open
  >
    <template v-slot:content>
      <form @submit.prevent="saveForm" data-cy="contact-form">
        <div class="flex-container">
          <div class="flex__left">
            <div v-if="formHasErrors">
              <b>Please correct the following error(s):</b>
              <ul>
                <li
                  v-for="error in formErrors"
                  :key="error"
                  data-cy="form-error"
                >
                  {{ error }}
                </li>
              </ul>
            </div>
            <div class="form-row">
              <label for="job-title" data-cy="job-title-label">
                Job Title
              </label>
              <input
                v-model="contactJobTitle"
                id="job-title"
                name="job-title"
                type="text"
                class="form-control"
                maxlength="100"
                data-cy="job-title-input"
              />
            </div>
            <div class="form-row">
              <label for="Title" data-cy="title-label">Title</label>
              <select
                v-model="contact.Title"
                id="Title"
                name="title"
                class="form-control"
                style="width: 160px"
                data-cy="title-select"
              >
                <option value="Mr.">Mr.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Ms.">Ms.</option>
                <option value="Dr.">Dr.</option>
                <option value="Esq.">Esq.</option>
              </select>
            </div>
            <div class="form-row">
              <label for="firstName" data-cy="first-name-label">
                First Name
              </label>
              <input
                v-model="contact.Firstname"
                :disabled="customerContact != null"
                id="firstName"
                name="firstName"
                type="text"
                class="form-control"
                data-cy="first-name-input"
              />
            </div>
            <div class="form-row">
              <label for="lastName" data-cy="last-name-label">
                Last Name
              </label>
              <input
                v-model="contact.Lastname"
                :disabled="customerContact != null"
                id="lastName"
                name="lastName"
                type="text"
                class="form-control"
                data-cy="last-name-input"
              />
            </div>
            <div class="form-row">
              <label for="email" data-cy="email-label"> Email </label>
              <input
                v-model="contact.Email"
                id="email"
                name="email"
                type="text"
                class="form-control"
                data-cy="email-input"
              />
            </div>
            <div class="form-row">
              <label for="primaryPhone" data-cy="primary-phone-label">
                Primary Phone
              </label>
              <input
                v-model="contact.Voice"
                id="primaryPhone"
                name="primaryPhone"
                type="text"
                class="form-control"
                data-cy="primary-phone-input"
              />
            </div>
            <div class="form-row">
              <label for="secondaryPhone" data-cy="secondary-phone-label">
                Secondary Phone
              </label>
              <input
                v-model="contact.Cell"
                id="secondaryPhone"
                name="secondaryPhone"
                type="text"
                class="form-control"
                data-cy="secondary-phone-input"
              />
            </div>
            <div class="form-row">
              <label for="fax" data-cy="fax-label"> Fax </label>
              <input
                v-model="contact.Fax"
                id="fax"
                name="fax"
                type="text"
                class="form-control"
                data-cy="fax-input"
              />
            </div>
            <div class="form-row">
              <label for="additionalInfo" data-cy="info-label">
                Additional Info
              </label>
              <input
                v-model="contact.Otherinfo"
                id="additionalInfo"
                name="additionalInfo"
                type="text"
                class="form-control"
                data-cy="info-input"
              />
            </div>
            <div class="form-row">
              <label></label>
              <button
                class="btn btn-success"
                :disabled="
                  formSubmitting ||
                  !dataFacilities ||
                  !dataFacilities.find((x) => x.IsAssigned == true)
                "
                data-cy="save-contact-button"
              >
                Save Customer Contact
              </button>
            </div>
          </div>
          <div class="flex__right grid-container">
            <div v-if="errorFacilities" class="error">
              {{ errorFacilities }}
            </div>
            <div v-else-if="!dataFacilities">Loading...</div>
            <Grid
              v-else
              :style="{ height: 'auto' }"
              :columns="columns"
              :data-items="dataFacilities"
              ref="gridRef"
              data-cy="facilities-grid"
            >
              <template
                v-slot:myTemplate="{
                  props: { dataItem, field, colSpan, className },
                }"
              >
                <td :col-span="colSpan" :class="className">
                  <input
                    :checked="dataItem[field]"
                    :disabled="disableCheckbox(field, dataItem)"
                    type="checkbox"
                    @change="handleOnChange(dataItem)"
                    data-cy="facility-checkbox"
                  />
                </td>
              </template>
              <template v-slot:Name="{ props: { dataItem, field } }">
                <td class="text-left">
                  {{ dataItem[field] }}
                </td>
              </template>
              <template v-slot:City="{ props: { dataItem, field } }">
                <td class="text-left">
                  {{ dataItem[field] }}
                </td>
              </template>
              <template v-slot:State="{ props: { dataItem, field } }">
                <td class="text-left">
                  {{ dataItem[field] }}
                </td>
              </template>
              <template v-slot:Id="{ props: { dataItem } }">
                <td class="text-left">
                  {{
                    AssigneContactTypes(
                      dataItem["IsWebContact"],
                      dataItem["IsPrimaryContact"],
                      dataItem["IsBillingContact"]
                    )
                  }}
                </td>
              </template>
            </Grid>
          </div>
        </div>
      </form>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { api, type ErrorResponse } from "@/api";
import { Grid } from "@progress/kendo-vue-grid";
import type { CustomerContact } from "@/Domain/CustomerContact";
import {
  CreateAddOrModifyCustomerContactRequest,
  CreateDeleteCustomerContactRequest,
} from "@/api/customerContacts";
import type { Facility } from "@/Domain/Facility";
import {
  CreateAddOrModifyContactRequest,
  CreateEmptyContact,
} from "@/api/contacts";
import type { Contact } from "@/Domain/Contact";
import { useMainStore } from "@/stores/useMainStore";
import {
  RCorRcSupportStaffGroup,
  RfrGroup,
  OUInternalStaff,
} from "@/shared/access/RoleGroups";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import GridController from "@/shared/services/GridController";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps({
  currentContact: {
    type: Object as () => CustomerContact,
    required: false,
  },
});

const emit = defineEmits<{
  (e: "contactUpdated", contact: Contact | undefined, error?: string): void;
  (e: "closeModal"): void;
}>();

const contact = ref<Contact>(
  props.currentContact ? props.currentContact?.Contact : CreateEmptyContact()
);

// customerContact to update. Undefined for a new contact
const customerContact = ref<CustomerContact | undefined>(props.currentContact);
const contactJobTitle = ref<string>(customerContact.value?.companyTitle ?? "");

// place to save a copy so we can see if it changed
const emailOriginal = contact.value?.Email;

// place to save a copy so we can see if it changed
let facilitiesOriginal: Facility[];

// the list of facilities including checkboxes changed by the user ("IsAssigned" property)
const dataFacilities = ref<Facility[]>();
const errorFacilities = ref<Error>();

// form validation errors
const formErrors = ref<string[]>([]);
const formHasErrors = computed(() => formErrors.value.length > 0);
const formSubmitting = ref(false);

const gridRef = ref<InstanceType<typeof Grid> | null>(null);

const columns = ref([
  {
    title: " ",
    field: "IsAssigned",
    cell: "myTemplate",
    minWidth: 80,
  },
  {
    title: "Facility Name",
    field: "Name",
    cell: "Name",
    minWidth: 200,
  },
  {
    title: "City",
    field: "City",
    cell: "City",
    minWidth: 80,
  },
  {
    title: "State",
    field: "State",
    cell: "State",
    minWidth: 80,
  },
  {
    title: "Contact Types",
    field: "Id",
    cell: "Id",
    minWidth: 80,
  },
] as GridColumnPropsExtended[]);

const controller = new GridController<Contact>(
  "ContactDialogGrid",
  columns.value
);

controller.initOptions();

function validEmail(email: string) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

function validateForm() {
  formErrors.value = [];

  console.debug(
    "contact-dialog: validateForm(): email validation returns " +
      validEmail(contact.value.Email)
  );

  if (!contact.value.Firstname || !contact.value.Firstname.trim()) {
    formErrors.value.push("First Name required.");
  }
  if (!contact.value.Lastname || !contact.value.Lastname.trim()) {
    formErrors.value.push("Last Name required.");
  }
  if (!contact.value.Email || !contact.value.Email.trim()) {
    formErrors.value.push("Email required.");
  } else if (!validEmail(contact.value.Email)) {
    formErrors.value.push("Invalid Email address.");
  }

  if (!contact.value.Voice || !contact.value.Voice.trim()) {
    formErrors.value.push("Phone required.");
  }

  return !formHasErrors.value;
}

function disableCheckbox(fieldName: string, facility: Facility) {
  return (
    facility.IsWebContact ||
    facility.IsPrimaryContact ||
    facility.IsBillingContact
  );
}

function errorOnSaveForm(err: string) {
  console.error("contact-dialog: errorOnSaveForm() catch error = " + err);
  notificationStore.notify({
    type: NotificationTypeEnum.error,
    message: "Error happened while saving contact. Please try again.",
    duration: 5000,
  });
  emit("contactUpdated", undefined, err);
}

async function saveForm() {
  try {
    formSubmitting.value = true;

    if (!validateForm()) return;

    if (contact.value.Email != emailOriginal)
      contact.value.Enteredby = "DoNotUpdate";

    const contactPromise = api.post(
      "AddOrModifyContact",
      CreateAddOrModifyContactRequest(
        main.userLogin,
        main.companyLogin,
        contact.value
      )
    );

    let contactId = -1;
    await handleResponsePromiseAsync(
      contactPromise,
      (data) => {
        contactId = data;
      },
      "ContactDialog.vue",
      "saveForm",
      (err) => errorOnSaveForm(err)
    );

    if (contact.value.Id <= 0) contact.value.Id = contactId;

    if (!dataFacilities.value || contactId <= 0) {
      emit("contactUpdated", contact.value);
      return;
    }
    // sort facilities so that new assigned ones are processed first
    const facilities = dataFacilities.value.sort((f) =>
      f.IsAssigned ? -1 : 1
    );
    for (const facility of facilities) {
      const originalFacility = facilitiesOriginal.find(
        (f) => f.Id == facility.Id
      );
      if (!facility.IsAssigned && originalFacility!.IsAssigned) {
        await api.get(
          "DeleteCustomerContact",
          CreateDeleteCustomerContactRequest(
            main.userLogin,
            main.companyLogin,
            facility.Name,
            originalFacility!.AssignedOtherCcOrPcId || 0
          )
        );
        continue;
      }
      const jobTitleChanged =
        customerContact.value &&
        customerContact.value.companyTitle != contactJobTitle.value;
      if (
        facility.IsAssigned &&
        (!originalFacility!.IsAssigned || jobTitleChanged)
      ) {
        await api.post(
          "AddOrModifyCustomerContact",
          CreateAddOrModifyCustomerContactRequest(
            main.userLogin,
            main.companyLogin,
            !originalFacility!.IsAssigned,
            facility.Id.toString(),
            facility.Name,
            contact.value.Id,
            contactJobTitle.value,
            customerContact.value
          )
        );
      }
    }

    notificationStore.notify({
      message: `Contact ${contact.value.Firstname} ${contact.value.Lastname} was updated successfully`,
      type: NotificationTypeEnum.success,
      duration: 5000,
    });
    emit("contactUpdated", contact.value);
  } finally {
    formSubmitting.value = false;
  }
}

function closeModal() {
  // sometimes this goes to by-facility and sometimes to facilit-contact-grid (former is "Add" latter is "edit")
  emit("closeModal");
}

function success(resp: Facility[]) {
  dataFacilities.value = resp;

  // creare a copy of each object, not copies of the references.
  // See https://stackoverflow.com/questions/47776776/how-to-clone-array-of-objects-typescript?noredirect=1&lq=1
  if (dataFacilities.value)
    facilitiesOriginal = dataFacilities.value.map((obj) => ({ ...obj }));
}

function error(err: Error) {
  console.error("contact-dialog: error(): error = " + err);
  errorFacilities.value = err;
}

const includeMainOffice =
  !main.isInAnyRole(RfrGroup.roles) || // an RFR with additional non RFR roles can see main office
  main.isInAnyRole(RCorRcSupportStaffGroup.roles) ||
  main.isInAnyRole(OUInternalStaff.roles);

console.log("includeMainOffice: " + includeMainOffice);

api
  .get("GetFacilities", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    includeMainOffice: includeMainOffice,
    subApplication: 8,
    contactId: contact.value.Id > 0 ? contact.value.Id : -1,
    includeSpecial: false,
  })
  .then((resp) => {
    if (resp.success) {
      success(resp.response);
    } else {
      error((resp as ErrorResponse).error);
    }
  })
  .catch(error);

function AssigneContactTypes(
  IsWebContact: boolean,
  IsPrimaryContact: boolean,
  IsBillingContact: boolean
) {
  let ret = "";

  ret += IsWebContact ? "OUDirect" : "";
  ret += ret.length > 0 && IsPrimaryContact ? "/" : "";
  ret += IsPrimaryContact ? "Main" : "";
  ret += ret.length > 0 && IsBillingContact ? "/" : "";
  ret += IsBillingContact ? "Billing" : "";
  return ret;
}

function handleOnChange(facility: Facility) {
  facility.IsAssigned = !facility.IsAssigned;
}

watchEffect(() => {
  if (customerContact.value)
    contactJobTitle.value = customerContact.value.companyTitle;
});

watchEffect(() => {
  if (gridRef.value) {
    controller.setGridRef(gridRef);
  }
});
</script>

<style lang="scss">
.contact-dialog-content {
  max-width: 90vw;
}
</style>

<style scoped lang="scss">
dialog {
  width: 100%;
  height: 100%;
  top: 0;
  border: none;
  background: none;
}
dialog .modal-content {
  border: none;
  border-top: 7px solid #4a90e2;
  padding: 20px;
  width: 1200px;
  height: 580px;
  left: 50%;
  margin-left: -550px;
  position: fixed;
  top: 15vh;
  background-color: white;
  box-shadow: 0 2px 8px rgbal(0, 0, 0, 0.26);
  z-index: 9999;
}
.modal-content > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.modal__close {
  font-size: 18px;
  padding: 10px;
  cursor: pointer;
}
.modal__close:hover {
  color: #444;
}
dialog .modal-bg {
  position: fixed;
  z-index: 100;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.4);
}

.contact-dialog-content {
  .flex-container {
    display: flex;
    flex-wrap: nowrap;
    margin-top: 10px;
    flex-direction: column-reverse;
    gap: 16px;

    @include media-breakpoint-up(sm) {
      flex-direction: row;
      gap: 0;
    }
  }
}
.flex__left {
  flex: 1;
  margin-right: 40px;
}
.flex__right {
  flex: 3;
}
.grid-container {
  overflow: auto;
  max-height: 440px;
  width: 100%;
  border: 1px solid #ddd;

  @include media-breakpoint-up(sm) {
    width: unset;
  }
}
.form-row {
  display: flex;
  margin-bottom: 10px;
  align-items: center;
}
.form-row label {
  font-weight: 200;
  text-align: right;
  min-width: 125px;
  margin-right: 20px;
}
td,
:deep(th) {
  font-size: 13px;
}
</style>
